import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormMultiSelect } from '@/components/forms/form-multi-select';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { MultiSelectOption } from '@/components/ui/multi-select';
import { studentsTypeaheadSearch } from '@/lib/api-call-hooks';
import { getAdminSession, getSessionFeedbacks, updateSessionStudents } from '@/lib/api-calls';
import { AdminSession } from '@airreading/common/data';

export interface UpdateStudentsButtonProps {
  session: AdminSession;
  disabled?: boolean;
  trigger?: React.ReactNode;
}

const formSchema = z.object({
  students: z.array(z.object({ value: z.string(), label: z.string() })).min(1, 'At least one student must be selected'),
});
type FormData = z.infer<typeof formSchema>;

export function UpdateStudentsButton({ session, disabled, trigger }: UpdateStudentsButtonProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const schoolId = session.school?.id || session.studentGroupId;
  const studentsTypeahead = studentsTypeaheadSearch(queryClient, { schools: [schoolId] });

  const currentStudents: MultiSelectOption[] = useMemo(() => {
    return (session.students || []).map((s) => ({ value: s.id, label: s.fullNameOrEmail ?? '' }));
  }, [session.students]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (studentIds: string[]) => {
      await updateSessionStudents({
        sessionId: session.id,
        payload: {
          studentIds,
          shouldSendUpdateConfirmationEmail: true,
        },
      });
    },
  });

  const onSubmit = async ({ formData, closeModal }: SubmitProps<FormData>) => {
    try {
      await mutateAsync(formData.students.map((s) => s.value));
      toast({ title: 'Students updated' });
      queryClient.invalidateQueries(getAdminSession(session.id));
      queryClient.invalidateQueries(getSessionFeedbacks());
      closeModal();
    } catch {
      toast({ title: 'Failed to update students', variant: 'destructive' });
    }
  };

  return (
    <FormModal
      modalTitle="Update students"
      formSchema={formSchema}
      defaultValues={{ students: currentStudents }}
      trigger={
        trigger || (
          <Button variant="outline" className="bg-slate-100 hover:bg-slate-200" disabled={disabled}>
            Update students
          </Button>
        )
      }
      onSubmit={onSubmit}
      submitButtonProps={{ disabled: disabled || isPending, loading: isPending }}
      confirmBtnLabel="Update"
      cancelBtnLabel="Cancel"
    >
      {({ form }) => (
        <FormMultiSelect
          form={form}
          name="students"
          label="Students"
          className="w-full"
          multipleSelectorProps={{
            disabled,
            onSearch: studentsTypeahead,
          }}
        />
      )}
    </FormModal>
  );
}
